import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Home, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

// South African ID Luhn checksum validation
function luhnCheck(id) {
    if (!/^\d{13}$/.test(id)) return false;
    let sum = 0;
    let alt = false;
    for (let i = 12; i >= 0; i--) {
        let n = parseInt(id[i], 10);
        if (alt) { n *= 2; if (n > 9) n -= 9; }
        sum += n;
        alt = !alt;
    }
    return sum % 10 === 0;
}

const SA_BANKS = [
    { name: "ABSA", code: "632005" },
    { name: "African Bank", code: "430000" },
    { name: "Bidvest Bank", code: "462005" },
    { name: "Capitec Bank", code: "470010" },
    { name: "Discovery Bank", code: "679000" },
    { name: "FNB (First National Bank)", code: "250655" },
    { name: "Investec Bank", code: "580105" },
    { name: "Mercantile Bank", code: "450105" },
    { name: "Nedbank", code: "198765" },
    { name: "Sasfin Bank", code: "683000" },
    { name: "South African Post Bank (Postbank)", code: "460005" },
    { name: "Standard Bank", code: "051001" },
    { name: "TymeBank", code: "678910" },
];

function dobFromId(id) {
    if (!/^\d{13}$/.test(id)) return "";
    const yy = parseInt(id.slice(0, 2), 10);
    const mm = id.slice(2, 4);
    const dd = id.slice(4, 6);
    const currentYY = new Date().getFullYear() % 100;
    const year = yy <= currentYY ? 2000 + yy : 1900 + yy;
    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);
    if (m < 1 || m > 12 || d < 1 || d > 31) return "";
    return `${dd}/${mm}/${year}`;
}

const inp = (err) =>
    `w-full border px-4 py-3 text-sm bg-white text-tca-ink placeholder:text-tca-mute focus:outline-none focus:ring-2 focus:ring-tca-orange/50 ${
        err ? "border-red-400" : "border-tca-line"
    }`;
const disabled = "w-full border border-tca-line px-4 py-3 text-sm bg-gray-50 text-tca-mute cursor-not-allowed select-none";

function Field({ id, label, required, error, children }) {
    return (
        <div id={id}>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-tca-mute mb-1.5">
                {label}{required && <span className="text-tca-orange ml-1">*</span>}
            </label>
            {children}
            {error && <p className="text-[11px] text-red-600 mt-1" role="alert">{error}</p>}
        </div>
    );
}

function SectionCard({ letter, title, children }) {
    return (
        <div className="bg-white border border-tca-line p-6 sm:p-8 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-tca-line">
                <span className="w-8 h-8 flex items-center justify-center bg-tca-orange text-white font-display font-extrabold text-sm shrink-0">
                    {letter}
                </span>
                <h2 className="font-display font-bold text-tca-ink text-xl">{title}</h2>
            </div>
            {children}
        </div>
    );
}

export default function IT77Form() {
    const location = useLocation();
    const passed = location.state || {};

    const [form, setForm] = useState({
        surname: "",
        firstNames: passed.name || "",
        idNumber: "",
        dateOfBirth: "",
        email: passed.email || "",
        cell: "",
        homeTel: "",
        busTel: "",
        isMarried: "",
        marriageType: "",
        spouseInitials: "",
        spouseId: "",
        streetNo: "",
        streetName: "",
        unitComplex: "",
        suburb: "",
        city: "",
        postalCode: "",
        bankName: "",
        branchCode: "",
        accountType: "",
        accountNumber: "",
    });

    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    useEffect(() => {
        setForm((f) => ({ ...f, dateOfBirth: dobFromId(f.idNumber) }));
    }, [form.idNumber]);

    useEffect(() => {
        const bank = SA_BANKS.find((b) => b.name === form.bankName);
        setForm((f) => ({ ...f, branchCode: bank ? bank.code : "" }));
    }, [form.bankName]);

    const set = (k) => (e) => {
        let val = e.target.value;
        if (k === "streetNo") {
            const hasAlpha = /[^0-9]/.test(val);
            const digits = val.replace(/\D/g, "");
            setForm((f) => ({ ...f, streetNo: digits }));
            setErrors((er) => ({
                ...er,
                streetNo: hasAlpha ? "Tip: only digits allowed in this field to indicate the street number." : undefined,
            }));
            return;
        }
        setForm((f) => ({ ...f, [k]: val }));
        if (k === "postalCode") {
            const digits = val.replace(/\D/g, "");
            if (val.length === 0) {
                setErrors((er) => ({ ...er, postalCode: undefined }));
            } else if (digits.length < 4) {
                setErrors((er) => ({ ...er, postalCode: "Tip: the postal code should be exactly 4 digits." }));
            } else if (digits.length === 4) {
                setErrors((er) => ({ ...er, postalCode: undefined }));
            } else {
                setErrors((er) => ({ ...er, postalCode: "Postal code must be exactly 4 digits." }));
            }
        } else if (errors[k]) {
            setErrors((er) => ({ ...er, [k]: undefined }));
        }
        if (k === "email" && val.length > 0) {
            setErrors((er) => ({
                ...er,
                email: EMAIL_RE.test(val.trim()) ? undefined : "Please enter a valid email address (e.g. name@example.co.za).",
            }));
        }
        if (k === "cell" || k === "homeTel" || k === "busTel") {
            const stripped = val.replace(/\s/g, "");
            const tipMsg = k === "cell"
                ? "Tip: the cell number should be exactly 10 digits and start with a 0."
                : "Tip: the telephone number should be exactly 10 digits and start with a 0.";
            if (stripped.length === 0) {
                setErrors((er) => ({ ...er, [k]: undefined }));
            } else if (stripped.length < 10) {
                setErrors((er) => ({ ...er, [k]: tipMsg }));
            } else if (stripped.length === 10 && stripped.startsWith("0")) {
                setErrors((er) => ({ ...er, [k]: undefined }));
            } else if (!stripped.startsWith("0")) {
                setErrors((er) => ({ ...er, [k]: tipMsg }));
            } else {
                setErrors((er) => ({ ...er, [k]: tipMsg }));
            }
        }
        if (k === "idNumber") {
            if (val.length === 0) {
                setErrors((er) => ({ ...er, idNumber: undefined }));
            } else if (val.length < 13) {
                setErrors((er) => ({ ...er, idNumber: "Tip: the ID number needs to be exactly 13 digits." }));
            } else if (val.length === 13) {
                setErrors((er) => ({
                    ...er,
                    idNumber: luhnCheck(val)
                        ? undefined
                        : "This does not appear to be a valid South African ID number. Please double-check.",
                }));
            } else {
                setErrors((er) => ({ ...er, idNumber: "ID number must be exactly 13 digits." }));
            }
        }
    };

    const radio = (k, v) => () => {
        if (k === "isMarried" && v === "no") {
            setForm((f) => ({ ...f, isMarried: "no", marriageType: "", spouseInitials: "", spouseId: "" }));
        } else {
            setForm((f) => ({ ...f, [k]: v }));
        }
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (!form.surname.trim()) er.surname = "Surname is required.";
        if (!form.firstNames.trim()) er.firstNames = "First name(s) are required.";
        if (!/^\d{13}$/.test(form.idNumber) || !luhnCheck(form.idNumber)) er.idNumber = "This does not appear to be a valid South African ID number. Please double-check.";
        if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address.";
        const strippedCell = form.cell.replace(/\s/g, "");
        if (!strippedCell) er.cell = "Cell number is required.";
        else if (!strippedCell.startsWith("0")) er.cell = "Cell number must start with 0 (e.g. 0821234567).";
        else if (strippedCell.length !== 10) er.cell = "Cell number must be exactly 10 digits (e.g. 0821234567).";
        if (!form.isMarried) er.isMarried = "Please indicate your marital status.";
        if (form.isMarried === "yes" && !form.marriageType) er.marriageType = "Please select your type of marriage.";
        if (!form.streetNo.trim()) er.streetNo = "Street number is required.";
        if (!form.streetName.trim()) er.streetName = "Street name is required.";
        if (!form.suburb.trim()) er.suburb = "Suburb / district is required.";
        if (!form.city.trim()) er.city = "City / town is required.";
        if (!form.postalCode.trim()) er.postalCode = "Postal code is required.";
        else if (!/^\d{4}$/.test(form.postalCode.trim())) er.postalCode = "Postal code must be exactly 4 digits.";
        if (!form.bankName) er.bankName = "Please select your bank.";
        if (!form.accountType) er.accountType = "Please select an account type.";
        if (!form.accountNumber.trim()) er.accountNumber = "Account number is required.";
        return er;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            toast.error("Please fix the highlighted fields before submitting.");
            const firstId = Object.keys(er)[0];
            document.getElementById(`field-${firstId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        setStatus("loading");
        try {
            await fetch(`${API_URL}/api/it77/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    surname: form.surname.trim(),
                    firstNames: form.firstNames.trim(),
                    idNumber: form.idNumber,
                    dateOfBirth: form.dateOfBirth || null,
                    email: form.email.trim(),
                    cell: form.cell.replace(/\s/g, ""),
                    homeTel: form.homeTel || null,
                    busTel: form.busTel || null,
                    isMarried: form.isMarried,
                    marriageType: form.marriageType || null,
                    spouseInitials: form.spouseInitials || null,
                    spouseId: form.spouseId || null,
                    streetNo: form.streetNo.trim() || null,
                    streetName: form.streetName.trim(),
                    unitComplex: form.unitComplex || null,
                    suburb: form.suburb.trim(),
                    city: form.city.trim(),
                    postalCode: form.postalCode.trim(),
                    bankName: form.bankName,
                    branchCode: form.branchCode,
                    accountType: form.accountType,
                    accountNumber: form.accountNumber.trim(),
                }),
            }).then((r) => { if (!r.ok) throw new Error(r.status); });
            setStatus("success");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Submission failed. Please try again or contact admin@tcauditors.co.za directly.");
        }
    };

    if (status === "success") {
        return (
            <div className="bg-tca-fog min-h-screen flex items-center justify-center p-6" data-testid="it77-success">
                <div className="bg-white border border-tca-line p-8 max-w-lg w-full text-center">
                    <CheckCircle2 className="w-12 h-12 text-tca-orange mx-auto mb-4" />
                    <h2 className="font-display font-extrabold text-tca-ink text-2xl mb-3">Submitted Successfully</h2>
                    <p className="text-tca-mute text-sm leading-relaxed mb-6">
                        Thank you, <strong className="text-tca-ink">{form.firstNames} {form.surname}</strong>. Your IT77 Tax Registration Form has been sent to our team. We will send status updates and feedback to{" "}
                        <strong className="text-tca-ink">{form.email}</strong> within 1–4 business hours.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/" className="btn-secondary text-sm" data-testid="it77-success-home">Back to Home</Link>
                        <Link to="/tax-number" className="btn-primary text-sm" data-testid="it77-success-tax">Back to Get Your Tax Number</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-it77-form">

            {/* Top navigation */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <Link to="/" className="btn-ghost text-sm gap-2" data-testid="it77-back-home">
                        <Home className="w-4 h-4" /> Back to Home
                    </Link>
                    <span className="text-tca-line hidden sm:inline">|</span>
                    <Link to="/tax-number" className="btn-ghost text-sm gap-2" data-testid="it77-back-tax-number">
                        <ArrowLeft className="w-4 h-4" /> Back to Get Your Tax Number
                    </Link>
                </div>
            </div>

            {/* Header */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-10 max-w-3xl">
                    <p className="overline text-tca-orange mb-3">SARS IT77 · Individual Registration</p>
                    <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl leading-[1.02]">
                        Complete Your IT77<br />Form Electronically
                    </h1>
                    <p className="mt-4 text-tca-mute leading-relaxed">
                        Fill in your details below and our team will process your SARS tax registration or lookup on your behalf.
                        Fields marked <span className="text-tca-orange font-semibold">*</span> are required.
                    </p>
                </div>
            </div>

            {/* Form */}
            <div className="container-tca py-10 max-w-3xl">
                <form onSubmit={onSubmit} noValidate data-testid="it77-form">

                    {/* Section A — Personal Particulars */}
                    <SectionCard letter="A" title="Personal Particulars">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field id="field-surname" label="Surname" required error={errors.surname}>
                                <input
                                    type="text"
                                    value={form.surname}
                                    onChange={set("surname")}
                                    placeholder="e.g. Van der Merwe"
                                    className={inp(errors.surname)}
                                    data-testid="it77-surname"
                                />
                            </Field>
                            <Field id="field-firstNames" label="First Names (first two)" required error={errors.firstNames}>
                                <input
                                    type="text"
                                    value={form.firstNames}
                                    onChange={set("firstNames")}
                                    placeholder="e.g. John Peter"
                                    className={inp(errors.firstNames)}
                                    data-testid="it77-first-names"
                                />
                            </Field>
                            <Field label="Identification Type">
                                <input
                                    type="text"
                                    value="South African ID"
                                    disabled
                                    className={disabled}
                                    data-testid="it77-id-type"
                                />
                            </Field>
                            <Field id="field-idNumber" label="South African ID Number" required error={errors.idNumber}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={13}
                                    value={form.idNumber}
                                    onChange={set("idNumber")}
                                    placeholder="13-digit ID number"
                                    className={inp(errors.idNumber)}
                                    data-testid="it77-id-number"
                                />
                            </Field>
                            <Field label="Date of Birth (auto-calculated from ID)">
                                <input
                                    type="text"
                                    value={form.dateOfBirth || "— enter ID number above —"}
                                    disabled
                                    className={disabled}
                                    data-testid="it77-dob"
                                />
                            </Field>
                            <Field id="field-cell" label="Cell Number" required error={errors.cell}>
                                <input
                                    type="tel"
                                    value={form.cell}
                                    onChange={set("cell")}
                                    placeholder="e.g. 0821234567"
                                    maxLength={10}
                                    className={inp(errors.cell)}
                                    data-testid="it77-cell"
                                />
                            </Field>
                            <Field id="field-email" label="Contact Email" required error={errors.email}>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="e.g. name@example.co.za"
                                    className={inp(errors.email)}
                                    data-testid="it77-email"
                                />
                            </Field>
                            <Field label="Home Telephone (optional)" error={errors.homeTel}>
                                <input
                                    type="tel"
                                    value={form.homeTel}
                                    onChange={set("homeTel")}
                                    placeholder="e.g. 0112345678"
                                    maxLength={10}
                                    className={inp(errors.homeTel)}
                                    data-testid="it77-home-tel"
                                />
                            </Field>
                            <Field label="Business Telephone (optional)" error={errors.busTel}>
                                <input
                                    type="tel"
                                    value={form.busTel}
                                    onChange={set("busTel")}
                                    placeholder="e.g. 0112345678"
                                    maxLength={10}
                                    className={inp(errors.busTel)}
                                    data-testid="it77-bus-tel"
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    {/* Section B — Marital Status */}
                    <SectionCard letter="B" title="Marital Status">
                        <Field id="field-isMarried" label="Are you married?" required error={errors.isMarried}>
                            <div className="flex gap-6 mt-1">
                                {[{ v: "yes", label: "Yes" }, { v: "no", label: "No" }].map(({ v, label }) => (
                                    <label key={v} className="flex items-center gap-2.5 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={form.isMarried === v}
                                            onChange={radio("isMarried", v)}
                                            className="accent-tca-orange w-4 h-4"
                                            data-testid={`it77-married-${v}`}
                                        />
                                        <span className="text-sm text-tca-ink">{label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>

                        {form.isMarried === "yes" && (
                            <div className="mt-6 space-y-5 pt-5 border-t border-tca-line">
                                <Field id="field-marriageType" label="Type of Marriage" required error={errors.marriageType}>
                                    <div className="flex flex-col sm:flex-row gap-5 mt-1">
                                        {[
                                            { v: "community", label: "In Community of Property" },
                                            { v: "outcommunity", label: "Out of Community of Property" },
                                        ].map(({ v, label }) => (
                                            <label key={v} className="flex items-center gap-2.5 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    checked={form.marriageType === v}
                                                    onChange={radio("marriageType", v)}
                                                    className="accent-tca-orange w-4 h-4"
                                                    data-testid={`it77-marriage-type-${v}`}
                                                />
                                                <span className="text-sm text-tca-ink">{label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </Field>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Spouse's Initials (optional)">
                                        <input
                                            type="text"
                                            value={form.spouseInitials}
                                            onChange={set("spouseInitials")}
                                            placeholder="e.g. J.P."
                                            className={inp(false)}
                                            data-testid="it77-spouse-initials"
                                        />
                                    </Field>
                                    <Field label="Spouse's ID Number (optional)">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={13}
                                            value={form.spouseId}
                                            onChange={set("spouseId")}
                                            placeholder="13-digit ID number"
                                            className={inp(false)}
                                            data-testid="it77-spouse-id"
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}
                    </SectionCard>

                    {/* Section C — Physical Address */}
                    <SectionCard letter="C" title="Physical Address">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field id="field-streetNo" label="Street Number" required error={errors.streetNo}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={form.streetNo}
                                    onChange={set("streetNo")}
                                    placeholder="e.g. 42"
                                    className={inp(errors.streetNo)}
                                    data-testid="it77-street-no"
                                />
                            </Field>
                            <Field id="field-streetName" label="Street Name" required error={errors.streetName}>
                                <input
                                    type="text"
                                    value={form.streetName}
                                    onChange={set("streetName")}
                                    placeholder="e.g. Main Road"
                                    className={inp(errors.streetName)}
                                    data-testid="it77-street-name"
                                />
                            </Field>
                            <Field label="Unit No. / Complex (optional)">
                                <input
                                    type="text"
                                    value={form.unitComplex}
                                    onChange={set("unitComplex")}
                                    placeholder="e.g. Unit 5, Sunrise Complex"
                                    className={inp(false)}
                                    data-testid="it77-unit-complex"
                                />
                            </Field>
                            <Field id="field-suburb" label="Suburb / District" required error={errors.suburb}>
                                <input
                                    type="text"
                                    value={form.suburb}
                                    onChange={set("suburb")}
                                    placeholder="e.g. Sandton"
                                    className={inp(errors.suburb)}
                                    data-testid="it77-suburb"
                                />
                            </Field>
                            <Field id="field-city" label="City / Town" required error={errors.city}>
                                <input
                                    type="text"
                                    value={form.city}
                                    onChange={set("city")}
                                    placeholder="e.g. Johannesburg"
                                    className={inp(errors.city)}
                                    data-testid="it77-city"
                                />
                            </Field>
                            <Field id="field-postalCode" label="Postal Code" required error={errors.postalCode}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={form.postalCode}
                                    onChange={set("postalCode")}
                                    placeholder="e.g. 1960"
                                    className={inp(errors.postalCode)}
                                    data-testid="it77-postal-code"
                                />
                            </Field>
                            <Field label="Country">
                                <input
                                    type="text"
                                    value="South Africa"
                                    disabled
                                    className={disabled}
                                    data-testid="it77-country"
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    {/* Section D — Banking Details */}
                    <SectionCard letter="D" title="Banking Details">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field id="field-bankName" label="Bank Name" required error={errors.bankName}>
                                <select
                                    value={form.bankName}
                                    onChange={set("bankName")}
                                    className={`${inp(errors.bankName)} appearance-none`}
                                    data-testid="it77-bank-name"
                                >
                                    <option value="">— Select your bank —</option>
                                    {SA_BANKS.map((b) => (
                                        <option key={b.name} value={b.name}>{b.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Universal Branch Code (auto-populated)">
                                <input
                                    type="text"
                                    value={form.branchCode || "— select bank above —"}
                                    disabled
                                    className={disabled}
                                    data-testid="it77-branch-code"
                                />
                            </Field>
                            <Field id="field-accountType" label="Account Type" required error={errors.accountType}>
                                <div className="flex flex-col gap-3 mt-1">
                                    {[
                                        { v: "current-cheque", label: "Current/Cheque" },
                                        { v: "transmission", label: "Transmission" },
                                        { v: "savings", label: "Savings" },
                                    ].map(({ v, label }) => (
                                        <label key={v} className="flex items-center gap-2.5 cursor-pointer">
                                            <input
                                                type="radio"
                                                checked={form.accountType === v}
                                                onChange={radio("accountType", v)}
                                                className="accent-tca-orange w-4 h-4"
                                                data-testid={`it77-account-type-${v}`}
                                            />
                                            <span className="text-sm text-tca-ink">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </Field>
                            <Field id="field-accountNumber" label="Account Number" required error={errors.accountNumber}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={form.accountNumber}
                                    onChange={set("accountNumber")}
                                    placeholder="e.g. 4075231234"
                                    className={inp(errors.accountNumber)}
                                    data-testid="it77-account-number"
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="it77-submit"
                        className="btn-primary w-full justify-center gap-2 disabled:opacity-60"
                    >
                        {status === "loading" ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                        ) : (
                            "Submit Registration to TC Auditors"
                        )}
                    </button>
                    <p className="text-[11px] text-tca-mute mt-3 text-center leading-relaxed">
                        By submitting, you consent to TC Auditors using these details to process your SARS tax registration or lookup. We never share your information.
                    </p>
                </form>
            </div>
        </div>
    );
}
